import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BuildingOfficeIcon, 
  UserGroupIcon,
  ChartBarIcon,
  PlusIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import departmentService from '../../services/departmentService';
import DepartmentList from './DepartmentList';
import DepartmentForm from './DepartmentForm';
import DepartmentHierarchy from './DepartmentHierarchy';
import DepartmentStats from './DepartmentStats';

const DepartmentManagement = () => {
  const [activeTab, setActiveTab] = useState('list');
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [filters, setFilters] = useState({
    search: '',
    isActive: '',
    parentId: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  useEffect(() => {
    loadDepartments();
  }, [filters, pagination.page]);

  const loadDepartments = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...filters
      };
      
      const response = await departmentService.getDepartments(params);
      if (response.success) {
        console.log('Departments API Response:', response.data.departments);
        setDepartments(response.data.departments);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
          pages: response.data.pagination.pages
        }));
      }
    } catch (error) {
      console.error('Error loading departments:', error);
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateDepartment = () => {
    setEditingDepartment(null);
    setShowForm(true);
  };

  const handleEditDepartment = (department) => {
    setEditingDepartment(department);
    setShowForm(true);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingDepartment(null);
  };

  const handleFormSuccess = () => {
    handleFormClose();
    loadDepartments();
    toast.success(editingDepartment ? 'Department updated successfully' : 'Department created successfully');
  };

  const handleDeleteDepartment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department? This action cannot be undone.')) {
      return;
    }

    try {
      await departmentService.deleteDepartment(id);
      toast.success('Department deleted successfully');
      loadDepartments();
    } catch (error) {
      console.error('Error deleting department:', error);
      toast.error('Failed to delete department');
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      const blob = await departmentService.exportDepartments(format);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `departments.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success(`Departments exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting departments:', error);
      toast.error('Failed to export departments');
    }
  };

  const tabs = [
    {
      id: 'list',
      name: 'Department List',
      icon: BuildingOfficeIcon,
      description: 'View and manage all departments'
    },
    {
      id: 'hierarchy',
      name: 'Organization Chart',
      icon: UserGroupIcon,
      description: 'Visual department hierarchy'
    },
    {
      id: 'stats',
      name: 'Department Statistics',
      icon: ChartBarIcon,
      description: 'Analytics and reports'
    }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'list':
        return (
          <DepartmentList
            departments={departments}
            loading={loading}
            pagination={pagination}
            filters={filters}
            onFiltersChange={setFilters}
            onPaginationChange={setPagination}
            onEdit={handleEditDepartment}
            onDelete={handleDeleteDepartment}
            onRefresh={loadDepartments}
          />
        );
      case 'hierarchy':
        return <DepartmentHierarchy />;
      case 'stats':
        return <DepartmentStats />;
      default:
        return (
          <DepartmentList
            departments={departments}
            loading={loading}
            pagination={pagination}
            filters={filters}
            onFiltersChange={setFilters}
            onPaginationChange={setPagination}
            onEdit={handleEditDepartment}
            onDelete={handleDeleteDepartment}
            onRefresh={loadDepartments}
          />
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Department Management</h1>
              <p className="mt-2 text-gray-600">
                Manage organizational structure and department hierarchy
              </p>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => handleExport('csv')}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                Export
              </button>
              <button
                onClick={handleCreateDepartment}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Department
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2
                    ${activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <tab.icon className="h-5 w-5" />
                  <span>{tab.name}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {renderTabContent()}
        </motion.div>

        {/* Department Form Modal */}
        {showForm && (
          <DepartmentForm
            department={editingDepartment}
            onClose={handleFormClose}
            onSuccess={handleFormSuccess}
          />
        )}
      </div>
    </div>
  );
};

export default DepartmentManagement; 